import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AppService } from '../app.service';
import { OrderStatusTag } from '../models/common/order-status-tag.model';
import { Warehouse } from '../models/common/warehouse.model';
import { CreateOrder, ModelColorSize } from '../models/photographer/create-order.model';
import { CommentItem } from '../shared/models/comment-item.model';
import { NewCommentItem } from '../shared/models/new-comment-item.model';
import { StudiosHttpClientService } from '../shared/services/studios-http-client.service';
import { StyleGroup } from 'src/app/components/company/models/style-group.type';
import { StyleDescription } from 'src/app/components/company/models/style-description.type';
import { Team } from 'src/app/components/company/models/team.type';
import { Purpose } from 'src/app/components/company/models/purpose.type';
import { Ids } from '../models/common/ids.model';
import { UploadedFile } from '../shared/models/uploaded-file.model';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class CommonService {

  private baseUrl: string;
  readonly #httpClient = inject(HttpClient);

  constructor(
    private appService: AppService,
    private studiosHttpClient: StudiosHttpClientService
  ) {
    this.baseUrl = this.appService.utilsUrls.apiUrl;
  }

  uploadFiles(formData: FormData): Observable<Array<string>> {
    const url = this.baseUrl + '/Common/UploadFiles';
    return this.#httpClient.post<Array<string>>(url, formData, {});
  }

  public createOrder(photographerId: number, warehouseId: number, stylesColorsSizes: ModelColorSize[], endDate: string, companyId?: number, comments?: string, loaderId?: string): Observable<number> {
    const body = new CreateOrder({
      PhotographerId: photographerId,
      Comments: comments || '',
      StylesColorsSizes: stylesColorsSizes,
      WarehouseId: warehouseId,
      EndDate: endDate,
      CompanyId: companyId >= 0 ? companyId : null
    });
    const url = this.baseUrl + '/Common/CreateOrder';
    return this.studiosHttpClient.post<number>(url, body, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
      }
    }, loaderId);
  }

  public getWarehouses(paginationPage: number, paginationSize: number, search: string, loaderId?: string, warehouseIds?: Array<Ids>): Observable<Array<Warehouse>> {
    const url = this.baseUrl + '/Common/GetWarehouses';
    return this.studiosHttpClient.post<Warehouse[]>(url, warehouseIds || [], {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public getOrderStatus(loaderId?: string): Observable<Array<OrderStatusTag>> {
    const url = this.baseUrl + '/Common/GetOrderStatus';
    return this.studiosHttpClient.get<Array<OrderStatusTag>>(url, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getStyleShotsComments(orderId: number, paginationPage: number, paginationSize: number, modelId: number, colorId: number, sizeGroupId?: number, loaderId?: string): Observable<Array<CommentItem>> {
    const url = this.baseUrl + '/Common/GetStyleShotsComments';
    return this.studiosHttpClient.get<Array<CommentItem>>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        orderId: orderId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        modelId: modelId.toString(),
        colorId: colorId.toString(),
        sizeGroupId: sizeGroupId >= 0 && sizeGroupId !== null ? sizeGroupId.toString() : ''
      }
    }, loaderId);
  }

  public insertStyleShotsComment(orderId: number, text: string, imgPath: string, modelId: number, colorId: number, sizeGroupId?: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Common/InsertStyleShotsComment';
    const body = new NewCommentItem({
      ColorId: colorId,
      ImgPath: imgPath || null,
      ModelId: modelId,
      OrderId: orderId,
      SizeGroupId: sizeGroupId >= 0 ? sizeGroupId : null,
      Text: text
    })
    return this.studiosHttpClient.post<number>(url, body, {
      params: {
        currentUserId: this.appService.user.userId.toString()
      }
    }, loaderId);
  }

  public getOrderComments(orderId: number, paginationPage: number, paginationSize: number, loaderId?: string): Observable<CommentItem[]> {
    const url = this.baseUrl + '/Common/GetOrderComments';
    return this.studiosHttpClient.get<CommentItem[]>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        orderId: orderId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString()
      }
    }, loaderId);
  }

  public insertOrderComment(orderId: number, text: string, imgPath: string, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Common/InsertOrderComment';
    const body = new NewCommentItem({
      ImgPath: imgPath || null,
      OrderId: orderId,
      Text: text
    })
    return this.studiosHttpClient.post<number>(url, body, {
      params: {
        currentUserId: this.appService.user.userId.toString()
      }
    }, loaderId);
  }

  public getStyleGroups(pageNumber: number = 0, paginationSize: number = 50, search: string = '', loaderId?: string, styleGroupIds?: Array<Ids>): Observable<Array<StyleGroup>> {
    const url = this.baseUrl + '/Company/GetStyleGroups';
    return this.studiosHttpClient.post<Array<StyleGroup>>(url, styleGroupIds || [], {
      params: {
        paginationPage: pageNumber.toString(),
        paginationSize: paginationSize.toString(),
        search: search
      }
    }, loaderId);
  }

  public getStyleDescriptions(paginationPage: number = 0, paginationSize: number = 50, search: string = '', loaderId?: string, styleDescriptionIds?: Array<Ids>): Observable<Array<StyleDescription>> {
    const url = this.baseUrl + '/Company/GetStyleDescriptions';
    return this.studiosHttpClient.post<Array<StyleDescription>>(url, styleDescriptionIds || [], {
      params: {
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search
      }
    }, loaderId);
  }

  public getTeams(paginationPage: number = 0, paginationSize: number = 50, search: string = '', loaderId?: string, teamIds?: Array<Ids>): Observable<Array<Team>> {
    const url = this.baseUrl + '/Company/GetTeams';
    return this.studiosHttpClient.post<Array<Team>>(url, teamIds || [], {
      params: {
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search
      }
    }, loaderId);
  }

  public getPurposes(paginationPage: number = 0, paginationSize: number = 50, search: string = '', loaderId?: string, purposeIds?: Array<Ids>): Observable<Array<Purpose>> {
    const url = this.baseUrl + '/Company/GetPurposes';
    return this.studiosHttpClient.post<Array<Purpose>>(url, purposeIds || [], {
      params: {
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search
      }
    }, loaderId);
  }
}
